import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import { inject, isDevMode, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { concat, EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { API_URL } from '@core/tokens/api-url.token';
import { GoneKind, RevalidationService } from '@core/revalidation/revalidation.service';

/**
 * The public GET calls whose responses go into the static page (A2, #999). Each was checked to give
 * the same answer to everyone, or a difference the first view may show anonymously (#999,
 * 2026-10-22). `/settings` is left out on purpose: maintenance mode must always be fresh. Anything
 * not listed is an ordinary request.
 */
const PUBLIC_PATHS: readonly RegExp[] = [
  /^\/public\/navigation$/,
  /^\/nodes\/\d+\/page$/,
  /^\/public\/associations$/,
  /^\/public\/associations\/slug\/[^/]+$/,
  /^\/public\/associations\/\d+\/(locations|recurring-bookings)$/,
  /^\/districts$/,
  /^\/resources\/slug\/[^/]+$/,
  /^\/public\/resources\/\d+\/children$/,
  /^\/public\/subscriptions\/locations$/,
  /^\/public\/locations\/\d+\/subscriptions$/,
  /^\/locations\/active-locations$/,
  /^\/locations\/\d+\/opening-hours$/,
  /^\/activities$/,
  /^\/activities\/slug\/[^/]+$/,
  /^\/activities\/locations-mapping$/,
];

/** Detail calls whose 404 on revalidation means the page was deleted since the render. */
const DETAIL_PATHS: readonly { kind: GoneKind; pattern: RegExp }[] = [
  { kind: 'association', pattern: /^\/public\/associations\/slug\/([^/]+)$/ },
  { kind: 'resource', pattern: /^\/resources\/slug\/([^/]+)$/ },
  { kind: 'activity', pattern: /^\/activities\/slug\/([^/]+)$/ },
];

interface StoredResponse {
  body: unknown;
}

/**
 * Stale-while-revalidate across hydration (A2, #999).
 * - Server: each public response is stored in TransferState.
 * - Browser, first view: the stored response is replayed at once, so hydration matches the static
 *   HTML; the same request then goes to the network as an ordinary call (with the token for a
 *   signed-in user), and its response is emitted only if it differs. A failed refresh keeps the
 *   page; a 404 on a detail call marks the page as deleted (RevalidationService.isGone).
 * - After the first view, or for anything not in PUBLIC_PATHS: an ordinary request.
 * See .claude/specs/abook-customer-app/core/a2-rendering.md § Interceptoren.
 */
export const revalidatingTransferInterceptor: HttpInterceptorFn = (req, next) => {
  const path = publicPath(req, inject(API_URL));
  if (path === null) return next(req);

  const transferState = inject(TransferState);
  const key = makeStateKey<StoredResponse>(`rv:${req.urlWithParams}`);

  if (isPlatformServer(inject(PLATFORM_ID))) {
    return next(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.status === 200) transferState.set(key, { body: event.body });
      }),
    );
  }

  const revalidation = inject(RevalidationService);
  const stored = revalidation.replaying ? transferState.get(key, null) : null;
  if (!stored) return next(req);
  transferState.remove(key);
  log('served the stored response', req.urlWithParams);

  const storedJson = JSON.stringify(stored.body);
  const fresh: Observable<HttpEvent<unknown>> = next(req).pipe(
    filter((event): event is HttpResponse<unknown> => event instanceof HttpResponse),
    filter((event) => {
      const unchanged = JSON.stringify(event.body) === storedJson;
      log(unchanged ? 'fresh response unchanged, ignored' : 'fresh response changed, updated', req.urlWithParams);
      if (!unchanged) revalidation.markChanged();
      return !unchanged;
    }),
    catchError((error: unknown) => {
      const gone = error instanceof HttpErrorResponse && error.status === 404 ? deletedDetail(path) : null;
      if (gone) {
        log('deleted since the render', req.urlWithParams);
        revalidation.markGone(gone.kind, gone.slug);
      } else {
        log('refresh failed, kept the stored response', req.urlWithParams);
      }
      return EMPTY;
    }),
  );

  return concat(of(new HttpResponse({ body: stored.body, status: 200, statusText: 'OK', url: req.urlWithParams })), fresh);
};

/** The request's path under the API when it is a public GET, else null. */
function publicPath(req: HttpRequest<unknown>, apiUrl: string): string | null {
  if (req.method !== 'GET' || !req.url.startsWith(apiUrl)) return null;
  const path = new URL(req.url).pathname.slice(new URL(apiUrl).pathname.replace(/\/$/, '').length);
  return PUBLIC_PATHS.some((pattern) => pattern.test(path)) ? path : null;
}

function deletedDetail(path: string): { kind: GoneKind; slug: string } | null {
  for (const { kind, pattern } of DETAIL_PATHS) {
    const slug = path.match(pattern)?.[1];
    if (slug) return { kind, slug: decodeURIComponent(slug) };
  }
  return null;
}

function log(message: string, url: string): void {
  if (isDevMode()) console.info(`[revalidate] ${message}: ${url}`);
}
